import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';
import type { AdminUser } from './auth';
import { discardUpload, uploadFile } from './uploads';

/**
 * Changing your own name and your own password.
 *
 * Until this existed, the only way to change a password was to press "Forgot
 * password" on your own account and wait for an email — a detour through a flow
 * built for people who cannot get in, taken by somebody already in. The display
 * name had no path at all.
 */

/** Why a change did not happen. Null means it did. */
export type PasswordProblem = 'WRONG_PASSWORD' | 'WEAK_PASSWORD' | 'UNCHANGED';

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      email
      name
      role
      avatarUrl
    }
  }
`;

export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { currentPassword: string; newPassword: string }) =>
      (await gqlClient.request<{ changePassword: PasswordProblem | null }>(CHANGE_PASSWORD, {
        input,
      })).changePassword,
    /*
     * The session list is stale whether or not it worked in the way the caller
     * expected: a successful change ends every other session. Refreshed here
     * rather than on the screen, so the two cannot disagree about it.
     */
    onSuccess: (problem) => {
      if (!problem) void queryClient.invalidateQueries({ queryKey: ['mySessions'] });
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) =>
      (await gqlClient.request<{ updateProfile: AdminUser }>(UPDATE_PROFILE, { input: { name } }))
        .updateProfile,
    // The header shows the name, so it has to hear about this.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
};

const SET_MY_AVATAR = gql`
  mutation SetMyAvatar($fileId: ID) {
    setMyAvatar(fileId: $fileId) {
      id
      email
      name
      role
      avatarUrl
    }
  }
`;

/**
 * Setting your own photo, or taking it off when given nothing.
 *
 * Your own, and there is no argument for anybody else's: the server reads the
 * session. Owners changing a colleague's photo waits for the card that gives
 * roles meaning — until then there is nothing checking who is asking.
 *
 * The bytes go up in a request of their own and the id that comes back is what
 * the row points at, so a mutation that fails after the upload has to take the
 * file back with it.
 */
export const useMyAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (photo: File | null) => {
      const stored = photo ? await uploadFile(photo) : null;
      try {
        const data = await gqlClient.request<{ setMyAvatar: AdminUser }>(SET_MY_AVATAR, {
          fileId: stored?.id ?? null,
        });
        return data.setMyAvatar;
      } catch (error) {
        if (stored) await discardUpload(stored.id);
        throw error;
      }
    },
    /*
     * The server's copy, written straight into the cache rather than refetched:
     * the header and the profile card both read `me`, and a gap between them
     * would show the old picture in one and the new one in the other.
     */
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
    /*
     * `setQueryData`, not `invalidateQueries` like the name above it: the
     * mutation already answers with the whole account, so refetching it would
     * be a second round trip for something already in hand — and the gap
     * between the two is a header still showing the old picture.
     */
  });
};

/** What to say about a refused change. */
export const passwordProblemMessage = (problem: PasswordProblem): string => {
  switch (problem) {
    case 'WRONG_PASSWORD':
      return 'That is not your current password.';
    case 'UNCHANGED':
      return 'That is already your password. Choose a different one.';
    default:
      /*
       * The rule itself lives on the server and is not restated here — a second
       * copy of "at least N characters" is a second thing to keep in step with
       * the one that actually decides.
       */
      return 'That password is too easy to guess. Use a longer one, or several words.';
  }
};
